<template>
  <section class="z-card">
    <div class="col-8">
      <div class="alert alert-warning text-center">
        {{ $t('db.recipient.required_format') }}
      </div>
      <z-form ref="form" class="z-form z-form--vertical" @submit.native.prevent="save">
        <z-form-group class="row mb-4">
          <div class="col-6">
            <label>{{ $t('db.recipient.field.first_name') }}</label>
            <z-form-input :rules="{ max: 128 }">
              <i-input v-model="form.first_name" />
            </z-form-input>
          </div>
          <div class="col-6">
            <label>{{ $t('db.recipient.field.last_name') }}</label>
            <z-form-input :rules="{ max: 60 }">
              <i-input v-model="form.last_name" />
            </z-form-input>
          </div>
        </z-form-group>

        <z-form-group class="row mb-4">
          <div class="col-6">
            <label>{{ $t('db.recipient.field.number') }}</label>
            <z-form-input :rules="{ required: true, min: 3, max: 15, phone: true }">
              <i-input v-model="form.number" />
            </z-form-input>
          </div>
          <div class="col-6">
            <label>{{ $t('db.recipient.field.email') }}</label>
            <z-form-input :rules="{ email: true }">
              <i-input v-model="form.email" />
            </z-form-input>
          </div>
        </z-form-group>

        <z-form-group class="row mb-4">
          <div class="col-6">
            <label>{{ $t('db.recipient.field.country') }}</label>
            <z-form-input :rules="{ max: 30 }">
              <i-input v-model="form.country" />
            </z-form-input>
          </div>
          <div class="col-6">
            <label>{{ $t('db.recipient.field.city') }}</label>
            <z-form-input :rules="{ max: 30 }">
              <i-input v-model="form.city" />
            </z-form-input>
          </div>
        </z-form-group>

        <z-form-group class="row mb-4">
          <div class="col-6">
            <label>{{ $t('db.recipient.field.birthdate') }}</label>
            <z-form-input>
              <masked-input
                v-model="form.birthdate"
                class="ivu-input ivu-input-default"
                :placeholder="$t('db.recipient.birthdate_format')"
                :mask="{
                  pattern: '11.11',
                  formatCharacters: {
                    V: {
                      validate: (char) => /[a-jA-J]/.test(char)
                    }
                  }
                }"
                @blur.native="checkBirth"
              />
            </z-form-input>
          </div>

          <div class="col-6">
            <label>{{ $t('db.recipient.field.age') }}</label>
            <z-form-input :rules="{ min_value: 1, max_value: 99 }">
              <i-input v-model="form.age" />
            </z-form-input>
          </div>
        </z-form-group>

        <z-form-group class="row mb-4">
          <div class="col-6">
            <label>{{ $t('shared.component.select.sex.title') }}</label>
            <z-form-input>
              <i-select v-model="form.sex" :clearable="true">
                <i-option value="male" :label="$t('shared.component.select.sex.value.male')" />
                <i-option value="female" :label="$t('shared.component.select.sex.value.female')" />
              </i-select>
            </z-form-input>
          </div>

          <div class="col-6">
            <label>{{ $t('db.recipient.field.custom1') }}</label>
            <z-form-input :rules="{ max: 255 }">
              <i-input v-model="form.custom1" />
            </z-form-input>
          </div>
        </z-form-group>

        <z-form-group class="row mb-4">
          <div class="col-6">
            <label>{{ $t('db.recipient.field.custom2') }}</label>
            <z-form-input :rules="{ max: 255 }">
              <i-input v-model="form.custom2" />
            </z-form-input>
          </div>
          <div class="col-6">
            <label>{{ $t('db.recipient.field.custom3') }}</label>
            <z-form-input :rules="{ max: 255 }">
              <i-input v-model="form.custom3" />
            </z-form-input>
          </div>
        </z-form-group>

        <z-form-group class="row mb-4">
          <div class="col-6">
            <label>{{ $t('db.recipient.field.custom4') }}</label>
            <z-form-input :rules="{ max: 255 }">
              <i-input v-model="form.custom4" />
            </z-form-input>
          </div>
          <div class="col-6">
            <label>{{ $t('db.recipient.field.custom5') }}</label>
            <z-form-input :rules="{ max: 255 }">
              <i-input v-model="form.custom5" />
            </z-form-input>
          </div>
        </z-form-group>
      </z-form>
      <i-button
        slot="footer-ok"
        type="success"
        size="large"
        :loading="is_button_loading"
        @click="save"
      >
        {{ $t('shared.component.button.save') }}
      </i-button>
    </div>
  </section>
</template>

<script>
  import defaultsDeep from 'lodash-es/defaultsDeep';
  import cloneDeep from 'lodash-es/cloneDeep';
  import { RecipientService } from '@db/services/RecipientService';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import { mapActions } from 'vuex';
  import { DataBaseService } from '@db/services/DataBaseService';

  export default {
    mixins: [PageServerValidationResponseMixin],
    data: () => ({
      form: {},
      is_button_loading: false
    }),
    computed: {
      database_id() {
        return this.$route.params.database_id;
      },
      item() {
        return this.$route.params.item;
      },
      is_edit() {
        return this.item && this.item.id;
      }
    },

    watch: {
      item: {
        handler(val) {
          this.form = cloneDeep(
            defaultsDeep(val, {
              first_name: '',
              last_name: '',
              number: '',
              email: '',
              country: '',
              city: '',
              birthdate: null,
              sex: null
            })
          );
        },
        immediate: true,
        deep: true
      }
    },
    created() {
      const id = this.database_id;
      DataBaseService.getItem(id)
        .then((database) => {
          this.set_breadcrumbs(database);
        })
        .finally(() => this.endLoadingPage());
    },
    methods: {
      ...mapActions('shared/breadcrumbs', { add_breadcrumbs: 'add_item' }),
      set_breadcrumbs(database) {
        this.add_breadcrumbs({
          item: {
            title: database['name'],
            route: { name: 'db.database.edit', params: { id: database['id'] } }
          }
        });
        const current_breadcrumb = this.is_edit
          ? 'db.breadcrumb.database.recipient.edit'
          : 'db.breadcrumb.database.recipient.create';
        this.add_breadcrumbs({
          item: {
            title: current_breadcrumb,
            route: '#'
          }
        });
      },
      checkBirth() {
        const dayMonthRegExp = /\b(0?[1-9]|[1-2][0-9]|3[0-1])\b\.+\b(0?[1-9]|[1][0-2])\b/g;
        if (!dayMonthRegExp.test(this.form.birthdate)) {
          this.form.birthdate = '';
        }
      },
      get_params() {
        const { form } = this;
        return {
          first_name: form.first_name || null,
          last_name: form.last_name || null,
          number: form.number.slice(1, form.number.length),
          email: form.email || null,
          country: form.country || null,
          city: form.city || null,
          birthdate: form.birthdate || null,
          age: form.age || null,
          sex: form.sex || null,
          custom1: form.custom1 || null,
          custom2: form.custom2 || null,
          custom3: form.custom3 || null,
          custom4: form.custom4 || null,
          custom5: form.custom5 || null
        };
      },
      save() {
        this.$refs.form.validate().then((is_valid) => {
          if (is_valid) {
            this.is_button_loading = true;
            const { database_id, item } = this;
            const params = this.get_params();
            const request = this.is_edit
              ? RecipientService.editItem(database_id, item.id, params)
              : RecipientService.createItem(database_id, params);

            request
              .then(() =>
                this.$router.push({
                  name: 'db.database.edit',
                  params: {
                    id: this.database_id
                  }
                })
              )
              .catch((e) => {
                this.check_server_validation(e);
              })
              .finally(() => (this.is_button_loading = false));
          }
        });
      }
    }
  };
</script>

<style scoped>
  .ivu-date-picker {
    width: 100%;
  }
</style>
